import { Fragment, useState } from "react";
import { Dialog, Disclosure, Menu, Transition } from "@headlessui/react";
import {
  Bars3BottomLeftIcon,
  RectangleGroupIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Logo from "../../assets/images/86294Sized_AdVendoo_falt_RK_01.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LiaBullhornSolid } from "react-icons/lia";
import { baseURL } from "../../api/api";
import { FaUserCircle } from "react-icons/fa";
import { useUser } from "../../context/UserContext";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const CompanyLayout = ({ children, page, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentView, switchView, canSwitchToOwner, profileDetails } =
    useUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSwitchToOwner = () => {
    switchView("owner");
    navigate("/ownerdashboard", { state: { isViewSwitch: true } });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("currentView");
    navigate("/");
  };

  const navigation = [
    {
      name: "Dashboard",
      to: "/companydashboard",
      icon: RectangleGroupIcon,
      current: page === "companydashboard",
    },
    {
      name: "Company Profile",
      to: "/companyprofile",
      icon: UserCircleIcon,
      current: location.pathname === "/companyprofile",
    },
    {
      name: "Campaigns",
      to: "/Campaigns",
      icon: LiaBullhornSolid,
      current: location.pathname === "/Campaigns",
    },
  ];

  return (
    <>
      <div>
        {/* Mobile sidebar dialog (unchanged) */}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-40 md:hidden"
            onClose={setSidebarOpen}
          >
            {/* ... existing mobile sidebar code ... */}
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden md:fixed md:inset-y-0 md:flex md:w-48 md:flex-col z-50">
          <div className="w-64 flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5">
            <div className="flex flex-shrink-0 items-center px-4">
              <img className="w-full" src={Logo} alt="Your Company" />
            </div>
            <div className="mt-5 flex flex-grow flex-col">
              <nav className="flex-1 space-y-1 px-2 pb-4">
                {navigation.map((item) =>
                  !item.children ? (
                    <Link
                      key={item.name}
                      to={item.to}
                      className={classNames(
                        item.current
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                        "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.current
                            ? "text-gray-500"
                            : "text-gray-400 group-hover:text-gray-500",
                          "mr-3 flex-shrink-0 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  ) : (
                    <Disclosure as="div" key={item.name} className="space-y-1">
                      {/* ... existing disclosure code ... */}
                    </Disclosure>
                  )
                )}
              </nav>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col md:pl-60">
          <div className="sticky top-0 flex h-16 flex-shrink-0 bg-white shadow z-30">
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-yellow md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex flex-1 justify-between px-3">
              <div className="flex flex-1">
                <h1 className="px-5 text-gray-700 font-bold text-lg my-auto">
                  {title}
                </h1>
              </div>
              <div className="ml-4 flex items-center md:ml-6">
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="flex max-w-xs items-center rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow focus:ring-offset-2">
                      <span className="sr-only">Open user menu</span>
                      {profileDetails?.profileImage ? (
                        <>
                          <img
                            className="h-9 w-9 rounded-full"
                            src={`${baseURL}/s3/images/${profileDetails?.profileImage}`}
                            alt=""
                          />
                          <span className="text-gray-500 font-semibold text-sm px-3">
                            {profileDetails.firstName} {profileDetails.lastName}
                          </span>
                        </>
                      ) : (
                        <>
                          <FaUserCircle className="text-gray-300 text-2xl" />
                          <span className="text-gray-500 font-semibold text-sm px-2">
                            {profileDetails?.firstName}{" "}
                            {profileDetails?.lastName}
                          </span>
                        </>
                      )}
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right px-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {canSwitchToOwner && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={classNames(
                                active
                                  ? "bg-yellow text-black"
                                  : "text-gray-700",
                                "flex items-center w-full px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150"
                              )}
                              onClick={handleSwitchToOwner}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-2 text-black"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M17 16l4-4m0 0l-4-4m4 4H7"
                                />
                              </svg>
                              Switch to Owner
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              "w-full block px-4 py-2 text-sm text-gray-700 text-left"
                            )}
                            onClick={handleLogout}
                          >
                            Sign Out
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          <div className="p-4 bg-gray-50 min-h-screen">{children}</div>
        </div>
      </div>
    </>
  );
};

export default CompanyLayout;
